const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

export interface SkeletonProps {
  className?: string;
}

/** Base pulsing placeholder block. Size/shape via className. */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-xl bg-skeleton', className)}
    />
  );
}

export interface CardSkeletonProps {
  className?: string;
}

/** Placeholder for a grid card: image block + avatar circle + two text lines. */
export function CardSkeleton({ className }: CardSkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'overflow-hidden rounded-2xl border border-card-border bg-card',
        className,
      )}
    >
      {/* Plain pulse div: square corners (the base Skeleton's rounded-xl
          would win over a rounded-none override in Tailwind's CSS order). */}
      <div className="aspect-[4/3] w-full animate-pulse bg-skeleton" />
      <div className="flex items-center gap-3 p-3">
        <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    </div>
  );
}

export interface ListSkeletonProps {
  /** Number of placeholder cards. Default 8. */
  count?: number;
  variant?: 'grid' | 'list';
}

/**
 * Loading placeholder for catalog pages. `grid` (default) mirrors the
 * responsive card grid; `list` renders stacked horizontal rows.
 */
export function ListSkeleton({ count = 8, variant = 'grid' }: ListSkeletonProps) {
  if (variant === 'list') {
    return (
      <div aria-hidden="true" className="space-y-3">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-2xl border border-card-border bg-card p-3"
          >
            <Skeleton className="h-20 w-20 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

export interface RailSkeletonProps {
  /** Number of placeholder cards. Default 6. */
  count?: number;
}

/** Horizontal row of small card placeholders for home rails. */
export function RailSkeleton({ count = 6 }: RailSkeletonProps) {
  return (
    <div aria-hidden="true" className="flex gap-3 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="w-40 shrink-0">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="mt-2 h-3.5 w-3/4" />
          <Skeleton className="mt-1.5 h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}
